import { useEffect, useState } from 'react';
import AdaptiveLogo from '../../../../common/controls/AdaptiveLogo';
import { getCompanyImage, getUniversityImage } from '../../../../common/styles/Images';
import {
    formatDesignationDisplay,
    getDomainValueForCode,
    getFlexColDomainValueForCode,
    isEmpty,
} from '../../../../library/Core/SmartFunctions';
import { pickDisplayJob } from '../../../../library/Service/currentJobDisplay';

const isCurrentJob = (job: any) => job?.isCurrent === true || job?.isCurrent === 1;

const isPrimary = (job: any) =>
    job?.isPrimaryCurrent === true || Number(job?.isPrimaryCurrent ?? job?.isPrimary) === 1;

export function getCurrentCompanySlides(state: any) {
    const jobsList = state?.data?.jobsList ?? [];
    const workingStatusCode = state?.data?.profile?.workingStatusCode;
    const currentJobs = jobsList.filter(isCurrentJob);
    if (currentJobs.length < 2) return [];

    const { job: lead } = pickDisplayJob(currentJobs, undefined, workingStatusCode);
    const rest = currentJobs
        .filter((job: any) => job !== lead)
        .sort((a: any, b: any) => String(b?.startDate || '').localeCompare(String(a?.startDate || '')));

    return lead ? [lead, ...rest] : rest;
}

const domainLogo = (id: any, domain: string, state: any, imageKey: 'logo' | 'img') => {
    const flexCol = getFlexColDomainValueForCode(id, domain, state);
    if (isEmpty(flexCol)) return '';
    try {
        return JSON.parse(flexCol)[imageKey] || '';
    } catch {
        return '';
    }
};

const slideView = (job: any, state: any) => {
    const category = job?.jobCategory;
    let name = '';
    let logo = '';

    if (category === 'SELF_EMPLOYED_AND_FREELANCING') {
        logo = getCompanyImage(domainLogo(job?.jobRoleCode, 'FREELANCING_JOB_ROLE_CODE', state, 'img'));
        name =
            job?.companyId == null || job?.companyId === '' || Number(job?.companyId) === 0
                ? job?.otherCompany || 'Freelancer'
                : getDomainValueForCode(job?.companyId, 'COMPANY_CODE', state);
    } else if (category === 'TEACHING_AND_RESEARCH') {
        logo = getUniversityImage(domainLogo(job?.instituteId, 'INSTITUTE_DOMAIN', state, 'logo'));
        name =
            job?.instituteId == null || job?.instituteId === '' || Number(job?.instituteId) === 0
                ? job?.otherInstitute || ''
                : getDomainValueForCode(job?.instituteId, 'INSTITUTE_DOMAIN', state);
    } else {
        logo = getCompanyImage(domainLogo(job?.companyId, 'COMPANY_CODE', state, 'logo'));
        name =
            job?.companyId == null || job?.companyId === '' || Number(job?.companyId) === 0
                ? job?.otherCompany || ''
                : getDomainValueForCode(job?.companyId, 'COMPANY_CODE', state);
    }

    let designation = formatDesignationDisplay(job?.designation, state, {
        workingStatusCode: category || state?.data?.profile?.workingStatusCode,
    });
    if (!designation && category === 'PROFESSIONAL_PRACTICE' && job?.typeOfPractice) {
        const resolvedPractice = getDomainValueForCode(job.typeOfPractice, 'PROFESSIONAL_PRACTICE_TYPE', state);
        designation =
            resolvedPractice && String(resolvedPractice) !== String(job.typeOfPractice)
                ? resolvedPractice
                : String(job.typeOfPractice).replace(/_/g, ' ');
    }

    const sinceParts: string[] = [];
    if (job?.startDate) {
        // userapi sends the DATE as a UTC instant (2024-07-01 IST -> 2024-06-30T18:30Z), so read it in local
        // time like the work form does; a bare yyyy-mm-dd is taken as written.
        const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(job.startDate));
        const date = dateOnly
            ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
            : new Date(job.startDate);
        if (!Number.isNaN(date.getTime())) {
            const month = date.toLocaleString('en-US', { month: 'short' });
            sinceParts.push(`Since ${month} ${date.getFullYear()}`);
        }
    }
    const city = job?.cityCode ? getDomainValueForCode(job.cityCode, 'CITY_CODE', state) : '';
    if (city && String(city) !== String(job?.cityCode)) sinceParts.push(city);

    return { name, logo, designation, since: sinceParts.join(' · '), primary: isPrimary(job) };
};

const CurrentCompaniesCarousel = ({ state }: { state: any }) => {
    const jobs = getCurrentCompanySlides(state);
    const [index, setIndex] = useState(0);

    useEffect(() => {
        setIndex(0);
    }, [state?.data?.id, jobs.length]);

    if (jobs.length < 2) return null;

    const safeIndex = index >= jobs.length ? 0 : index;
    const view = slideView(jobs[safeIndex], state);
    const step = (delta: number) => setIndex((current) => (current + delta + jobs.length) % jobs.length);

    return (
        <div className="current-companies-card">
            <div className="current-companies-card__top">
                <div className="current-companies-card__label">
                    Current companies
                    <span className="current-companies-card__count">{jobs.length}</span>
                </div>
                <div className="current-companies-card__nav">
                    <span>
                        {safeIndex + 1} of {jobs.length}
                    </span>
                    <button type="button" aria-label="Previous company" onClick={() => step(-1)}>
                        ‹
                    </button>
                    <button type="button" aria-label="Next company" onClick={() => step(1)}>
                        ›
                    </button>
                </div>
            </div>
            <div className="current-companies-card__body">
                <AdaptiveLogo className="current-companies-card__logo" src={view.logo} alt={view.name} fallbackShape="square" />
                <div className="min-w-0">
                    <div className="current-companies-card__name">
                        <span>{view.name}</span>
                        {view.primary && <span className="current-companies-card__primary">Primary</span>}
                    </div>
                    {view.designation && (
                        <div className="alumni_profile_college_subject current-companies-card__role">
                            <i className="icon"></i>
                            <span>{view.designation}</span>
                        </div>
                    )}
                    {view.since && <div className="current-companies-card__since">{view.since}</div>}
                </div>
            </div>
        </div>
    );
};

export default CurrentCompaniesCarousel;
